import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const KINDS = [["in", "Übergabe (Einzug)"], ["out", "Rücknahme (Auszug)"]];

// Termin festlegen und Einladung (Brief oder E-Mail) über den normalen Versanddialog vorbereiten
export default function AppointmentInviteDialog({ open, onOpenChange, tenancyId, defaultKind = "in", onReady }) {
  const [kind, setKind] = useState(defaultKind);
  const [f, setF] = useState({ date: "", time: "", location: "", default_location: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setKind(defaultKind); }, [open, defaultKind]);
  useEffect(() => {
    if (!open) return;
    api.get(`/handover-appointments/tenancy/${tenancyId}`, { params: { kind } }).then(({ data }) => setF(data)).catch(() => {});
  }, [open, kind, tenancyId]);
  const submit = async () => {
    if (!f.date) { toast.error("Bitte ein Datum eintragen"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/handover-appointments/tenancy/${tenancyId}/invitation`, { kind, date: f.date, time: f.time || null, location: f.location || null });
      onOpenChange(false);
      onReady(data);
    } catch (e) { toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Einladung konnte nicht erstellt werden – bitte Datum und Uhrzeit prüfen"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="appointment-invite-dialog">
        <DialogHeader>
          <DialogTitle>Termin-Einladung an die Mieter</DialogTitle>
          <DialogDescription>Der Termin wird im Mietverhältnis gespeichert. Danach wählen Sie im Versanddialog Brief oder E-Mail.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2">
            {KINDS.map(([k, l]) => (
              <button key={k} type="button" onClick={() => setKind(k)} data-testid={`appointment-kind-${k}`}
                className={`flex-1 rounded-md border px-3 py-2 text-sm transition-colors ${kind === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>
          <div className="grid grid-cols-[minmax(0,1fr)_8rem] gap-3">
            <div><Label>Datum</Label><Input type="date" value={f.date || ""} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="appointment-date" /></div>
            <div><Label>Uhrzeit</Label><Input type="time" step={300} value={f.time || ""} onChange={(e) => setF({ ...f, time: e.target.value })} data-testid="appointment-time" /></div>
          </div>
          <div>
            <Label>Treffpunkt</Label>
            <Input value={f.location || ""} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder={f.default_location} data-testid="appointment-location" />
            {f.default_location && f.location !== f.default_location && (
              <button type="button" className="mt-1 text-xs text-slate-500 hover:text-slate-800 underline" onClick={() => setF({ ...f, location: f.default_location })} data-testid="appointment-location-reset">Standard verwenden: {f.default_location}</button>
            )}
          </div>
          {f.tenants?.length > 0 && <p className="text-xs text-slate-500" data-testid="appointment-tenants">Empfänger: {f.tenants.join(", ")}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={submit} disabled={busy} data-testid="appointment-invite-continue">{busy ? "Wird vorbereitet…" : "Weiter zu Brief / E-Mail"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
